// LayoutFixer.js - Component to ensure immediate layout fixes for existing spaces
import { useEffect, useState } from "react";
import {
  hasLayoutOverlaps,
  compactAndFixLayout,
} from "../../utils/layoutUtils";

/**
 * Component that applies immediate layout fixes to spaces when they load
 * Especially important for viewing mode where we can't save changes
 */
export const LayoutFixer = ({
  widgets,
  activeLayout,
  onFixedWidgets,
  isEnabled = true,
}) => {
  const [hasAppliedFix, setHasAppliedFix] = useState(false);

  useEffect(() => {
    if (!isEnabled || !widgets || widgets.length === 0) {
      return;
    }

    // Only fix mobile and tablet layouts
    if (activeLayout !== "mobile" && activeLayout !== "tablet") {
      return;
    }

    // Check if we need to apply fixes
    const hasOverlaps = hasLayoutOverlaps(widgets, activeLayout);
    const hasMobileIssues =
      activeLayout === "mobile" &&
      widgets.some((widget) => {
        const layout = widget.layout?.mobile;
        return layout && (layout.startColumn !== 0 || layout.columns !== 4);
      });

    if ((hasOverlaps || hasMobileIssues) && !hasAppliedFix) {
      console.log(
        `[Immediate Layout Fix] Applying ${activeLayout} fixes to ${widgets.length} widgets`
      );

      const fixedWidgets = compactAndFixLayout(widgets, activeLayout);

      if (onFixedWidgets) {
        onFixedWidgets(fixedWidgets);
      }

      setHasAppliedFix(true);
    }
  }, [widgets, activeLayout, onFixedWidgets, isEnabled, hasAppliedFix]);

  // Reset when layout changes
  useEffect(() => {
    setHasAppliedFix(false);
  }, [activeLayout]);

  // This component doesn't render anything
  return null;
};

/**
 * Hook version of the layout fixer for easier use
 */
export const useImmediateLayoutFix = (
  widgets,
  activeLayout,
  isEnabled = true
) => {
  const [fixedWidgets, setFixedWidgets] = useState(null);

  useEffect(() => {
    if (!isEnabled || !widgets || widgets.length === 0) {
      setFixedWidgets(null);
      return;
    }

    // Only fix mobile and tablet layouts
    if (activeLayout !== "mobile" && activeLayout !== "tablet") {
      setFixedWidgets(null);
      return;
    }

    // Check if we need to apply fixes
    const hasOverlaps = hasLayoutOverlaps(widgets, activeLayout);
    const hasMobileIssues =
      activeLayout === "mobile" &&
      widgets.some((widget) => {
        const layout = widget.layout?.mobile;
        return layout && (layout.startColumn !== 0 || layout.columns !== 4);
      });

    if (hasOverlaps || hasMobileIssues) {
      console.log(
        `[Immediate Hook Fix] Applying ${activeLayout} fixes to ${widgets.length} widgets`
      );
      const fixed = compactAndFixLayout(widgets, activeLayout);
      setFixedWidgets(fixed);
    } else {
      setFixedWidgets(null);
    }
  }, [widgets, activeLayout, isEnabled]);

  return fixedWidgets || widgets;
};

export default LayoutFixer;
